import {
  PFlex,
  PCheckboxWrapper,
  PHeadline,
  PTextFieldWrapper,
  PText,
  PButtonPure,
  PFlexItem
} from '@porsche-design-system/components-react'
import React from 'react'
import { useForm, FormProvider } from 'react-hook-form'

import * as AppApi from '../../../utils/api/Apps'
import * as ModuleApi from '../../../utils/api/Modules'
import * as SubAppApi from '../../../utils/api/SubApps'

import ApplicationInformation from '../../../utils/Applications'
import { ApplicationContext } from '../../../utils/Context/ApplicationContext'
import AppIDField from '../../AppIDField/AppIDField'
import DepartmentField from '../../DepartmentField/DepartmentField'
import IctoField from '../../IctoField/IctoField'
import InputWithButton from '../../InputWithButton/InputWithButton'
import DrawerFooter from '../Footer/DrawerFooter'

import { TOAST_TIME } from '../../../config/config'
import { PortalContext } from '../../../utils/PortalContext'

import styles from './drawereditapp.module.css'

interface NewApplications {
  icto: string
  appId: string
  appDescription: string
  department: string
  applicationManager: string
  applicationManagerEmail: string
  authorizedEmail: string
  businessResponsible: boolean
}

export default function DrawerEditApp() {
  const { setDrawerContent, drawerData, setLoading, setToastContent, setToast, setDrawerData, setError } =
    React.useContext(PortalContext)

  const { applications, setApplications } = React.useContext(ApplicationContext)
  const [authorizedEmails, setAuthorizedEmails] = React.useState([''])

  const methods = useForm<NewApplications>({
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  })

  React.useEffect(() => {
    const newArray: Array<string> = []
    if (drawerData.appInformation.members.length > 1) {
      drawerData.appInformation.members.map((val: { email: string }, ind: number) => {
        if (ind !== 0) {
          newArray.push(val.email)
        }
      })
      setAuthorizedEmails([...newArray])
    }
    methods.setValue('appDescription', drawerData.appInformation.title, {
      shouldValidate: true
    })
    methods.setValue('applicationManager', drawerData.appInformation.members[0].name, {
      shouldValidate: true
    })
    methods.setValue('applicationManagerEmail', drawerData.appInformation.members[0].email, {
      shouldValidate: true
    })
  }, [drawerData])

  const getParentApp = () => {
    let title
    if (drawerData.appInformation.type === 'subApp') {
      const index = applications.findIndex((app) => app.app === drawerData.appInformation.parents.idApp)
      title = applications[index].appDescription
    } else if (drawerData.appInformation.type === 'module') {
      const appIndex = applications.findIndex((app) => app.app === drawerData.appInformation.parents.idApp)
      const index = applications[appIndex].subApp.findIndex(
        (subAppo: { subAppId: number }) => subAppo.subAppId === drawerData.appInformation.parents.idSubApp
      )
      title = applications[appIndex].subApp[index].subAppDescription
    } else {
      title = ''
    }
    return title
  }

  const endEditProcess = () => {
    setToastContent('Die Informationen der App wurden angepasst!')
    setToast(true)
    setLoading(false)
    setDrawerContent('DetailsApp')
    setTimeout(() => {
      setToast(false)
    }, TOAST_TIME)
  }

  const editApplication = (data: NewApplications) => {
    setLoading(true)
    if (drawerData.appInformation.type === 'app') {
      const mainApp = {
        icto: data.icto,
        appId: data.appId,
        appDescription: data.appDescription,
        department: data.department || drawerData.appInformation.department,
        members: authorizedEmails,
        applicationManager: data.applicationManager,
        applicationManagerEmail: data.applicationManagerEmail,
        authorizedEmail: '',
        businessResponsible: data.businessResponsible || drawerData.appInformation.businessResponsible,
        id: drawerData.id,
        app: drawerData.app,
        configId: drawerData.configId,
        subApp: drawerData.subApp
      }
      AppApi.update(mainApp)
        .then((response) => response.json())
        .then((newApp) => {
          const index = applications.findIndex((app) => app.app === newApp.app)
          const appInfo = ApplicationInformation({ app: newApp })
          applications[index] = {
            ...newApp,
            appInformation: appInfo
          }
          setDrawerData(applications[index])
          setApplications(applications)
          endEditProcess()
        })
        .catch((err) => {
          setError(true)
          setToastContent(err.message)
          setToast(true)
        })
    } else if (drawerData.appInformation.type === 'subApp') {
      const newAppId = data.appId === '' ? drawerData.appInformation.appId : 'APP-' + data.appId
      const subApp = {
        idApp: drawerData.appInformation.parents.idApp,
        appId: newAppId,
        subAppDescription: data.appDescription,
        id: drawerData.appInformation.id,
        configId: drawerData.appInformation.configId,
        modules: drawerData.modules
      }
      SubAppApi.update(subApp)
        .then((response) => response.json())
        .then((updatedApp) => {
          const appIndex = applications.findIndex((app) => app.app === drawerData.appInformation.parents.idApp)
          const index = applications[appIndex].subApp.findIndex(
            (subAppo: { subAppId: number }) => subAppo.subAppId === updatedApp.subAppId
          )
          const appInfo = ApplicationInformation({ app: applications[appIndex], subApp: updatedApp })
          applications[appIndex].subApp[index] = {
            ...updatedApp,
            appInformation: appInfo
          }
          setDrawerData(applications[appIndex].subApp[index])
          setApplications(applications)
          endEditProcess()
        })
        .catch((err) => {
          setError(true)
          setToastContent(err.message)
          setToast(true)
        })
    } else {
      const newAppId = data.appId === '' ? drawerData.appInformation.appId : 'APP-' + data.appId
      const module = {
        idSubApp: drawerData.appInformation.parents.idSubApp,
        idApp: drawerData.appInformation.parents.idApp,
        appId: newAppId,
        moduleDescription: data.appDescription,
        department: data.department || drawerData.appInformation.department,
        businessResponsible: data.businessResponsible || drawerData.appInformation.businessResponsible,
        id: drawerData.appInformation.id,
        configId: drawerData.appInformation.configId
      }
      ModuleApi.update(module)
        .then((response) => response.json())
        .then((updatedModule) => {
          const appIndex = applications.findIndex((app) => app.app === drawerData.appInformation.parents.idApp)
          const subAppIndex = applications[appIndex].subApp.findIndex(
            (subApp: { subAppId: number }) => subApp.subAppId === drawerData.appInformation.parents.idSubApp
          )
          const index = applications[appIndex].subApp[subAppIndex].modules.findIndex(
            (modulo: { moduleId: number }) => modulo.moduleId === updatedModule.moduleId
          )
          const appInfo = ApplicationInformation({
            app: applications[appIndex],
            subApp: applications[appIndex].subApp[subAppIndex],
            module: updatedModule
          })
          applications[appIndex].subApp[subAppIndex].modules[index] = {
            ...updatedModule,
            appInformation: appInfo
          }
          setDrawerData(applications[appIndex].subApp[subAppIndex].modules[index])
          setApplications(applications)
          endEditProcess()
        })
        .catch((err) => {
          setError(true)
          setToastContent(err.message)
          setToast(true)
        })
    }
  }

  const addEmail = (event: React.MouseEvent) => {
    event.preventDefault()
    if (methods.watch('authorizedEmail') !== undefined && methods.watch('authorizedEmail') !== '') {
      const newArr = [methods.watch('authorizedEmail')]
      const lowerCaseMails = newArr.map((element) => {
        return element.toLowerCase()
      })
      if (authorizedEmails[0] !== '') {
        methods.setValue('authorizedEmail', '', { shouldDirty: true })
        return setAuthorizedEmails([...authorizedEmails, ...lowerCaseMails])
      }
      methods.setValue('authorizedEmail', '', { shouldDirty: true })
      return setAuthorizedEmails([...lowerCaseMails])
    }
  }

  const deleteEmail = (email: string) => {
    return function (event: React.MouseEvent) {
      event.preventDefault()
      const emailSelected = email
      const newArr = authorizedEmails.filter((email) => email !== emailSelected)
      methods.setValue('authorizedEmail', '', { shouldDirty: true })
      return setAuthorizedEmails([...newArr])
    }
  }

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      addEmail(e)
    }
  }

  const abortEdit = () => {
    setDrawerContent('DetailsApp')
  }

  React.useEffect(() => {
    methods.register('appDescription', { required: true })
    methods.register('applicationManager', { required: true })
    methods.register('applicationManagerEmail', {
      required: true,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Bitte E-Mail Format beachten.'
      }
    })
  }, [methods])

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={methods.handleSubmit(editApplication)} noValidate>
        <PFlex wrap='wrap' direction='column' justifyContent='space-between' className={styles.container}>
          <main className={styles.content}>
            {drawerData.appInformation.type !== 'app' && (
              <PFlex justifyContent='space-between' className={styles.appInfo}>
                <PText>
                  von <b>{getParentApp()}</b>
                </PText>
                <PText align='right'>{drawerData.appInformation.icto}</PText>
              </PFlex>
            )}
            <PHeadline variant='headline-5'>Identifikation</PHeadline>
            <PTextFieldWrapper
              label='Bezeichnung'
              className={styles.textfield}
              state={methods.errors.appDescription ? 'error' : 'none'}
              message='Das Feld wird benötigt.'
            >
              <input type='text' required name='appDescription' placeholder='Name' ref={methods.register} />
            </PTextFieldWrapper>
            {drawerData.appInformation.type === 'app' && <IctoField defaultValue={drawerData.appInformation.icto} />}
            <AppIDField defaultValue={drawerData.appInformation.appId} />

            {drawerData.appInformation.type === 'app' && (
              <React.Fragment>
                <PHeadline variant='headline-5' className={styles.subtitle}>
                  Ansprechpartner
                </PHeadline>
                <PTextFieldWrapper
                  label='Application Manager (FD)'
                  className={styles.textfield}
                  state={methods.errors.applicationManager ? 'error' : 'none'}
                  message='Das Feld wird benötigt.'
                >
                  <input
                    type='text'
                    required
                    name='applicationManager'
                    placeholder='Vorname Nachname'
                    ref={methods.register}
                  />
                </PTextFieldWrapper>
                <DepartmentField defaultValue={drawerData.appInformation.department} />
                <PTextFieldWrapper
                  label='E-Mail'
                  className={styles.textfield}
                  state={methods.errors.applicationManagerEmail ? 'error' : 'none'}
                  message={
                    methods.errors.applicationManagerEmail?.type === 'pattern'
                      ? methods.errors.applicationManagerEmail?.message
                      : 'Das Feld wird benötigt.'
                  }
                >
                  <input
                    type='text'
                    required
                    name='applicationManagerEmail'
                    placeholder='vorname.nachname@porsche.de'
                    ref={methods.register}
                  />
                </PTextFieldWrapper>
                <PCheckboxWrapper label='Vom Fachbereich verantwortet' className={styles.check}>
                  <input
                    type='checkbox'
                    name='businessResponsible'
                    defaultChecked={drawerData.appInformation.businessResponsible}
                    ref={methods.register}
                  />
                </PCheckboxWrapper>
                <PHeadline variant='headline-5'>Zugriffsberechtigt</PHeadline>
                <InputWithButton
                  textFieldMethods={methods}
                  buttonIcon='add'
                  textFieldName='authorizedEmail'
                  buttonFunction={addEmail}
                  textFieldPlaceholder='Person via E-Mail hinzufügen'
                  buttonDescription='addEmail'
                  textFieldOnKeyPress={handleKeyPress}
                  labelText='Hinzufügen'
                />
                <PFlex direction='column' className={styles.emailList}>
                  {authorizedEmails.length >= 1 &&
                    authorizedEmails.map(
                      (email, id) =>
                        email && (
                          <PFlexItem key={id} className={styles.email}>
                            <PText title={email} className={styles.emailListItem} ellipsis>
                              {email}
                            </PText>

                            <PButtonPure
                              className={styles.closeIcon}
                              icon='close'
                              hideLabel
                              onClick={deleteEmail(email)}
                            >
                              deleteEmail
                            </PButtonPure>
                          </PFlexItem>
                        )
                    )}
                </PFlex>
              </React.Fragment>
            )}
          </main>
          <DrawerFooter onClick={abortEdit} />
        </PFlex>
      </form>
    </FormProvider>
  )
}
